import { FC, forwardRef } from 'react';
import { type IconName } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames/bind';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { Theme } from '../../types/enums';
import RawTextLink from '../TextLink/RawTextLink';
import { TextLinkUnderlineStyle } from '../TextLink/TextLink';
import { filterIconTheme, filterTextLinkTheme } from '../utils/ThemeFilter/ThemeFilter';
import { AlertMessagePropsTypes, AlertMessageTheme } from './types/propsType';
import { IconAlignment, TextWeight, AlertMessageWidth } from './types/enums';
import styles from './AlertMessage.module.scss';

const cx = classNames.bind(styles);

function getThemeIcon(type: Theme, icon?: IconName) {
  if (icon) {
    return icon;
  }

  const typeToIcon: { [key: string]: IconName } = {
    [Theme.SUCCESS]: 'circle-check',
    [Theme.WARNING]: 'triangle-exclamation',
    [Theme.ERROR]: 'square-exclamation',
    [Theme.ACTION]: 'bolt',
    [Theme.GRAY]: 'bolt',
    [Theme.BLUE]: 'calendar-check',
  };

  return typeToIcon[type] || null;
}
const AlertMessage: FC<AlertMessagePropsTypes> = forwardRef<HTMLButtonElement, AlertMessagePropsTypes>(
  (
    {
      children,
      content,
      iconColor,
      hasIcon = true,
      theme = Theme.SUCCESS,
      onChevronClick,
      onClick,
      className = '',
      textLink,
      icon,
      iconStyle,
      iconAlignment = IconAlignment.DEFAULT,
      bgColor = '',
      textColor = '',
      width,
    }: AlertMessagePropsTypes,
    ref,
  ) => {
    const notificationIconCondition = getThemeIcon(theme, icon);
    const componentWidth = width || (textLink?.text ? AlertMessageWidth.FIT_CONTENT : AlertMessageWidth.FULL_WIDTH);

    const getIconStyle = () => {
      if (iconStyle) {
        return iconStyle;
      } else if (theme === Theme.ACTION || theme === Theme.GRAY) {
        return IconStyle.SOLID;
      }
      return IconStyle.REGULAR;
    };
    const CClassname = cx('alert-message', [theme, componentWidth], className);
    const CBodyClassname = cx('alert-message-main', [iconAlignment]);

    return (
      <button
        className={CClassname}
        type="button"
        data-testid="alert-message-test-id"
        ref={ref}
        onClick={onClick || onChevronClick}
        {...(bgColor ? { style: { backgroundColor: bgColor, color: textColor } } : '')}
      >
        <div className={CBodyClassname}>
          {hasIcon && notificationIconCondition && (
            <FAIcon
              iconName={icon ? icon : notificationIconCondition}
              iconStyle={getIconStyle()}
              theme={filterIconTheme(iconColor || theme)}
              className={styles['primary-icon']}
              // data-testid="alert-message-icon-testid"
            />
          )}
          <div className={styles['alert-message-body']} data-testid="alert-message-body-test-id">
            {content ? (
              <>
                {content.heading ? (
                  <span
                    className={styles['alert-message-body-heading']}
                    data-testid="alert-message-heading-test-id"
                    {...(textColor ? { style: { color: textColor } } : '')}
                  >
                    {content.heading}
                  </span>
                ) : (
                  <></>
                )}
                {content.detail ? (
                  <>
                    <br />
                    <span
                      className={styles['alert-message-body-detail']}
                      {...(textColor ? { style: { color: textColor } } : '')}
                    >
                      {content.detail}
                    </span>
                  </>
                ) : (
                  <></>
                )}
              </>
            ) : (
              children
            )}
          </div>
        </div>
        <span className={styles['alert-message-action']}>
          {textLink?.text ? (
            <RawTextLink
              href={textLink.href}
              text={textLink.text}
              theme={filterTextLinkTheme(theme)}
              onClick={textLink.onClick}
              underline={TextLinkUnderlineStyle.FIXED}
              className={styles['alert-message-text-link']}
              data-testid="alert-message-text-link-test-id"
            />
          ) : onClick || onChevronClick ? (
            <FAIcon
              iconName="chevron-right"
              theme={filterIconTheme(theme)}
              className={styles['alert-message-icon']}
              data-testid="alert-message-action-test-icon"
            />
          ) : null}
        </span>
      </button>
    );
  },
);
AlertMessage.displayName = 'AlertMessage';
export { TextWeight, IconStyle, IconAlignment, AlertMessageWidth };
export default AlertMessage;
export type { AlertMessageTheme, AlertMessagePropsTypes };
